import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth"; // Firebase Authentication
import Header from "../components/Header";
import Footer from "../components/Footer";

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: "",
    email: "",
    photoURL: "",
    age: "",
    birthday: "",
    gender: "",  // New state for gender
  });
  const [photoURLInput, setPhotoURLInput] = useState(""); // State for URL input
  const [selectedPhoto, setSelectedPhoto] = useState(""); // State for selected photo from defaults

  // Default profile pictures
  const defaultProfilePics = [
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/43c3947905bfc0f3a450/sm",
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/400fa889832b5634005f/sm",
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/485ab0ac9de69824173b/sm",
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/45beb5b372c937710712/sm",
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/4f0c8b4a02f02d1b4b73/sm",
    "https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/43deaf86e04b96807fcf/sm"
  ];

  // Fetch user data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        setUser(currentUser);
        setProfileData((prev) => ({
          ...prev,
          displayName: currentUser.displayName || "",
          email: currentUser.email || "",
          photoURL: currentUser.photoURL || "",
        }));

        // Fetch additional fields from Firestore
        const userDoc = doc(db, "users", currentUser.uid);
        const docSnapshot = await getDoc(userDoc);
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setProfileData((prev) => ({
            ...prev,
            age: data.age || "",
            birthday: data.birthday || "",
            gender: data.gender || "",  // Fetch the gender from Firestore
            displayName: data.displayName || prev.displayName,
            email: data.email || prev.email,
          }));
        }

        // Fetch profile picture from Firestore (if any)
        const profilePicDoc = doc(db, "profilePics", currentUser.uid);
        const profilePicSnapshot = await getDoc(profilePicDoc);
        if (profilePicSnapshot.exists()) {
          const profilePicData = profilePicSnapshot.data();
          setProfileData((prev) => ({
            ...prev,
            photoURL: profilePicData.photoURL || prev.photoURL,
          }));
        }
      }
    };

    fetchData();
  }, []);

  // Handle input changes during edit
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "birthday") {
      const age = calculateAge(value);
      setProfileData({ ...profileData, [name]: value, age });
    } else {
      setProfileData({ ...profileData, [name]: value });
    }
  };

  // Calculate the age based on birthday
  const calculateAge = (birthday) => {
    const birthDate = new Date(birthday);
    const age = new Date().getFullYear() - birthDate.getFullYear();
    return age;
  };

  // Handle selecting a photo (either default or custom URL)
  const handleSelectPhoto = (url) => {
    setSelectedPhoto(url); // Update selected photo state
    setProfileData((prev) => ({
      ...prev,
      photoURL: url, // Update the profileData state with the selected photo URL
    }));
  };

  // Handle saving the profile picture (either from default or custom URL)
  const handleSaveProfilePicture = async () => {
    try {
      let updatedPhotoURL;

      // If user selected a default image
      if (selectedPhoto) {
        updatedPhotoURL = selectedPhoto;
      }

      // If user entered a custom URL
      if (photoURLInput) {
        updatedPhotoURL = photoURLInput;
      }

      if (updatedPhotoURL) {
        // Save the photoURL to Firestore in the profilePics collection
        const profilePicDoc = doc(db, "profilePics", auth.currentUser.uid);
        await setDoc(profilePicDoc, { photoURL: updatedPhotoURL });

        return updatedPhotoURL;
      }
      return null;
    } catch (error) {
      console.error("Error saving profile picture:", error);
      alert("Failed to save profile picture. Please try again.");
    }
  };

  // Save updated profile data
  const handleSave = async () => {
    try {
      if (auth.currentUser) {
        // Save profile picture first
        const updatedPhotoURL = await handleSaveProfilePicture();

        // Update displayName and photoURL in Firebase Auth
        await updateProfile(auth.currentUser, {
          displayName: profileData.displayName,
          photoURL: updatedPhotoURL || profileData.photoURL,
        });

        // Save all data to Firestore
        const userDoc = doc(db, "users", auth.currentUser.uid);
        await setDoc(userDoc, {
          displayName: profileData.displayName,
          email: profileData.email,
          photoURL: updatedPhotoURL || profileData.photoURL,
          age: profileData.age,
          birthday: profileData.birthday,
          gender: profileData.gender,  // Save gender to Firestore
        }, { merge: true });

        setUser({ ...auth.currentUser }); // Update local user state
        setIsEditing(false); // Exit edit mode
        alert("Profile updated successfully.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-500 to-blue-700 text-white">
      <Header />

      {/* Main Content */}
      <main className="flex-grow p-6">
        <h2 className="text-3xl font-bold mb-6 text-center">Profile Page</h2>

        <div className="max-w-3xl mx-auto bg-white text-gray-800 rounded-lg shadow-lg p-6">
          {/* Profile Picture */}
          <div className="flex items-center justify-center mb-6">
            <img
              src={profileData.photoURL || "https://via.placeholder.com/150"} // Placeholder if no profile picture
              alt="Profile"
              className="w-32 h-32 rounded-full border-4 border-indigo-500 object-cover"
            />
          </div>

          {/* Profile Picture Editing (Only shown in Edit mode) */}
          {isEditing && (
            <div className="mb-4">
              <label className="font-semibold">Change Profile Picture</label>
              <div className="mt-4">
                {/* Default image options */}
                <div className="flex gap-4">
                  {defaultProfilePics.map((url, index) => (
                    <div key={index} className="relative">
                      <img
                        src={url}
                        alt={`Default ${index + 1}`}
                        className={`w-20 h-20 rounded-full border-4 ${selectedPhoto === url ? "border-indigo-500" : "border-gray-300"} cursor-pointer`}
                        onClick={() => handleSelectPhoto(url)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Profile Details */}
          <div>
            <div className="mb-4">
              <label className="font-semibold">Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              ) : (
                <p className="text-lg">{profileData.displayName || "N/A"}</p>
              )}
            </div>

            <div className="mb-4">
                <label className="font-semibold">Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    readOnly
                    className="w-full px-4 py-2 border rounded-lg bg-gray-200"
                  />
                ) : (
                  <p className="text-lg">{profileData.email || "N/A"}</p>
                )}
              </div>

            <div className="mb-4">
              <label className="font-semibold">Age</label>
              {isEditing ? (
                <input
                  type="number"
                  name="age"
                  value={profileData.age}
                  readOnly
                  className="w-full px-4 py-2 border rounded-lg bg-gray-200"
                />
              ) : (
                <p className="text-lg">{profileData.age || "N/A"}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="font-semibold">Birthday</label>
              {isEditing ? (
                <input
                  type="date"
                  name="birthday"
                  value={profileData.birthday}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              ) : (
                <p className="text-lg">{profileData.birthday || "N/A"}</p>
              )}
            </div>

            {/* Gender Toggle */}
            <div className="mb-4">
              <label className="font-semibold">Gender</label>
              {isEditing ? (
                <div className="flex gap-4 mt-2">
                  <button
                    className={`px-4 py-2 rounded-lg ${profileData.gender === "Male" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
                    onClick={() => setProfileData({ ...profileData, gender: "Male" })}
                  >
                    Male
                  </button>
                  <button
                    className={`px-4 py-2 rounded-lg ${profileData.gender === "Female" ? "bg-pink-500 text-white" : "bg-gray-200"}`}
                    onClick={() => setProfileData({ ...profileData, gender: "Female" })}
                  >
                    Female
                  </button>
                </div>
              ) : (
                <p className="text-lg">{profileData.gender || "N/A"}</p>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 mt-6">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
